<script lang="ts">
	// T13: Bosla has suspended this restaurant (UC-23). It covers the whole
	// till, above the idle lock, every sheet and the undo, and nothing beneath
	// it can be tapped. Nothing beneath it unmounts either, so the draft and a
	// sale that did not go through are still there when it clears.
	//
	// The contact is plain text: a till in kiosk mode opens neither mail nor
	// WhatsApp. The reason never shows; only Bosla's admins see it.

	import BrandMark from '../components/BrandMark.svelte';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import { message, type Language } from '../i18n/messages';
	import type { Suspension } from '../suspension.svelte';

	interface Props {
		language: Language;
		/** Restaurant · shop · till, when the till knows them. */
		where: string;
		contact: Suspension;
		checking: boolean;
		onCheck: () => void;
	}

	let { language, where, contact, checking, onCheck }: Props = $props();

	// Focus moves into the cover, so a keyboard or a screen reader is not
	// left on the inert screen beneath it.
	let cover: HTMLDivElement | undefined = $state();
	$effect(() => {
		cover?.focus();
	});
</script>

<div
	class="cover"
	role="alertdialog"
	aria-modal="true"
	aria-label={message(language, 'suspendedLabel')}
	tabindex="-1"
	bind:this={cover}
	data-testid="till-suspended"
>
	<div class="body">
		<BrandMark size={26} />
		<div class="head">
			{#if where}
				<span class="where" data-testid="till-suspended-where">{where}</span>
			{/if}
			<h1>{message(language, 'suspendedTillTitle')}</h1>
			<p class="tell">{message(language, 'suspendedTillTell')}</p>
		</div>
		<Card pad testId="till-suspended-contact">
			<div class="contact">
				<span class="label">{message(language, 'suspendedTillContact')}</span>
				<bdi dir="ltr" class="email" data-testid="till-suspended-email">{contact.supportEmail}</bdi>
				{#if contact.supportWhatsapp}
					<span class="whatsapp" data-testid="till-suspended-whatsapp">
						{message(language, 'suspendedWhatsapp')}
						<bdi dir="ltr" class="number">{contact.supportWhatsapp}</bdi>
					</span>
				{/if}
			</div>
		</Card>
		<div class="actions">
			<Button
				testId="till-suspended-check"
				label={message(language, 'suspendedCheck')}
				tone="secondary"
				size="lg"
				state={checking ? 'pending' : 'idle'}
				pendingLabel={message(language, 'suspendedChecking')}
				onPress={onCheck}
			/>
		</div>
		<!-- The till's order store hears of a resume at its next menu check,
		     every few minutes, so the cover may outlast Check again (#371). -->
		<p class="resumes" data-testid="till-suspended-resumes">
			{message(language, 'suspendedTillResumes')}
		</p>
	</div>
</div>

<style>
	.cover {
		outline: none;
		position: fixed;
		inset: 0;
		z-index: var(--z-cover);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--space-9);
		background: var(--surface-page);
		color: var(--text-strong);
		overflow-y: auto;
	}

	.body {
		inline-size: 100%;
		max-inline-size: 560px;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.head {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.where {
		font-size: var(--text-sm);
		color: var(--text-muted);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	.tell {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.contact {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
	}

	.label {
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.email {
		font-family: var(--font-mono);
		font-size: var(--text-md);
		font-weight: 700;
		color: var(--text-strong);
	}

	.whatsapp {
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
	}

	.number {
		font-family: var(--font-mono);
		font-weight: 700;
	}

	.actions {
		display: flex;
		align-items: center;
		gap: var(--space-5);
		flex-wrap: wrap;
	}

	.resumes {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-muted);
		text-wrap: pretty;
	}
</style>
